/**
 * Normalize any timestamp (ISO, US locale, JS Date.toString, canonical) to:
 *   MM/DD/YYYY HH:MM:SS  (24-hour, local time)
 * Falsy or unparseable values fall through to a placeholder / the original string.
 *
 * Use ONLY for values written month-first (the AuditLog sheet) or in
 * unambiguous formats (ISO). Subscriber dates are Israeli day-first — use
 * parseIsraeliDate / formatIsraeliTimestamp for those.
 */
export function formatTimestamp(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// Sheet subscriber dates are Israeli day-first (DD/MM/YYYY), but some rows
// come back as real Date cells that Google Sheets stored month-first (US
// spreadsheet locale). A join date can never be in the future, so ANY parse
// landing ahead of now gets its day/month swapped when the swap yields a
// plausible past date. Applies to every parse path, not just slash text.
const FUTURE_GRACE_MS = 36 * 60 * 60 * 1000;

function swapIfImpossiblyFuture(ts: number): number {
  if (ts <= Date.now() + FUTURE_GRACE_MS) return ts;
  const d = new Date(ts);
  const day = d.getDate();
  const month = d.getMonth() + 1;
  if (day > 12 || day === month) return ts; // day can't be a month / swap is a no-op
  const swapped = new Date(
    d.getFullYear(), day - 1, month,
    d.getHours(), d.getMinutes(), d.getSeconds(),
  ).getTime();
  return !Number.isNaN(swapped) && swapped <= Date.now() + FUTURE_GRACE_MS ? swapped : ts;
}

export function parseIsraeliDate(value: string | null | undefined): number | null {
  if (!value) return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T,]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (m) {
    let day = Number(m[1]);
    let month = Number(m[2]);
    if (month > 12 && day <= 12) [day, month] = [month, day]; // actually US MM/DD
    const ts = new Date(
      Number(m[3]), month - 1, day,
      Number(m[4] ?? 0), Number(m[5] ?? 0), Number(m[6] ?? 0),
    ).getTime();
    return Number.isNaN(ts) ? null : swapIfImpossiblyFuture(ts);
  }
  const ts = Date.parse(s); // ISO, JS Date.toString, and other native formats
  return Number.isNaN(ts) ? null : swapIfImpossiblyFuture(ts);
}

/** Render a subscriber timestamp as Israeli DD/MM/YYYY HH:MM:SS. */
export function formatIsraeliTimestamp(value: string | null | undefined, fallback = '—'): string {
  const ts = parseIsraeliDate(value);
  if (ts == null) return value ? String(value) : fallback;
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}
